import { Box, styled } from '@mui/material';
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import { UserAccount } from '../../../data/models/user-account.model';
import { PagedList } from '../../../data/models/paged-list.model';

const PREFIX = 'ClientUsersTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    margin: theme.spacing(0, 4),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface ClientUsersTableProps {
  users: PagedList<UserAccount>;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (val: number) => void;
  onPageSizeChange: (val: number) => void;
  onEditUser: (user: UserAccount) => void;
}

export default function ClientUsersTable({
  users,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditUser,
}: ClientUsersTableProps) {
  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 250, sortable: false },
    { field: 'email', headerName: 'Email', width: 350, sortable: false },
    { field: 'role', headerName: 'Role', width: 300, sortable: false },
  ];

  const rows = users.items.map((user) => {
    const roles =
      user.userAccountRoles?.map((r) => r.userRoleName).filter(Boolean) ?? [];

    roles.sort();

    return {
      id: user.userAccountID!,
      name: `${user.userFirstName ?? ''} ${user.userLastName ?? ''}`.trim(),
      email: user.emailAddress,
      role: roles.join(', ') || '—',
    };
  });

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== page - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const handleRowClick = (params: any) => {
    const user = users.items.find((u) => u.userAccountID === params.id);
    if (user) {
      onEditUser(user as UserAccount);
    }
  };

  return (
    <StyledBox className={classes.container}>
      <DataGridPro
        rows={rows}
        getRowId={(row) => row.id}
        columns={columns}
        loading={loading}
        autoHeight
        pagination
        paginationMode="server"
        rowCount={users.itemCount}
        pageSizeOptions={[10, 20, 50]}
        paginationModel={{
          page: users.currentPage - 1,
          pageSize,
        }}
        onPaginationModelChange={paginationChange}
        disableColumnMenu
        disableColumnSelector
        checkboxSelection={false}
        className={classes.grid}
        onRowClick={handleRowClick}
      />
    </StyledBox>
  );
}
